export type PasswordContext = {
  email?: string | null;
  fullName?: string | null;
};

export type PasswordRequirementKey =
  | 'length'
  | 'uppercase'
  | 'lowercase'
  | 'number'
  | 'symbol'
  | 'common'
  | 'personal';

export type PasswordRequirementResult = {
  key: PasswordRequirementKey;
  label: string;
  met: boolean;
};

const COMMON_PASSWORDS = new Set([
  '12345678',
  '123456789',
  '1234567890',
  'password',
  'password1',
  'password12',
  'password123',
  'qwerty123',
  'qwerty123!',
  'letmein123',
  'welcome1',
  'welcome123',
  'worklynx1',
  'worklynx123',
]);

const SYMBOL_PATTERN = /[!@#$%^&*()_+\-=[\]{};'\\:"|<>?,./`~]/;

function normalise(value: string | null | undefined) {
  return value?.toLowerCase().trim() ?? '';
}

function contextTokens(context?: PasswordContext) {
  const email = normalise(context?.email);
  const fullName = normalise(context?.fullName);
  const emailParts = email.split('@')[0]?.split(/[._+\-\d]+/).filter(Boolean) ?? [];
  const nameParts = fullName.split(/\s+/).filter(Boolean);

  return [...emailParts, ...nameParts].filter((token) => token.length >= 3);
}

export function getPasswordRequirements(
  password: string,
  context?: PasswordContext,
): PasswordRequirementResult[] {
  const lowerPassword = password.toLowerCase();
  const compactPassword = lowerPassword.replace(/[^a-z0-9]/g, '');
  const personalTokens = contextTokens(context);
  const hasPersonalToken = personalTokens.some((token) => lowerPassword.includes(token));

  return [
    {
      key: 'length',
      label: 'At least 8 characters',
      met: password.length >= 8,
    },
    {
      key: 'uppercase',
      label: 'One uppercase letter',
      met: /[A-Z]/.test(password),
    },
    {
      key: 'lowercase',
      label: 'One lowercase letter',
      met: /[a-z]/.test(password),
    },
    {
      key: 'number',
      label: 'One number',
      met: /\d/.test(password),
    },
    {
      key: 'symbol',
      label: 'One special character',
      met: SYMBOL_PATTERN.test(password),
    },
    {
      key: 'common',
      label: 'Not a common password',
      met: !COMMON_PASSWORDS.has(lowerPassword) && !COMMON_PASSWORDS.has(compactPassword),
    },
    {
      key: 'personal',
      label: 'Does not include your name or email',
      met: personalTokens.length === 0 || !hasPersonalToken,
    },
  ];
}

export function getPasswordIssueMessages(password: string, context?: PasswordContext) {
  return getPasswordRequirements(password, context)
    .filter((requirement) => !requirement.met)
    .map((requirement) => requirement.label);
}

export function isStrongPassword(password: string, context?: PasswordContext) {
  return getPasswordIssueMessages(password, context).length === 0;
}
